import { ReduxState } from "../../..";
import {
  createTemplate,
  getTemplate,
  updateTemplate,
} from "../../../../docuclipper/api";
import { Document, EdgeType } from "../../../../docuclipper/DocuclipperTypes";
import { logErrorToGA } from "../../../../docuclipper/Logger";
import { sleep } from "../../../../utils/utils";
import { createAlert } from "../../Alerts";
import { setDocument } from "../../Files";
import {
  deleteAllFields,
  fetchEdgesFromWords,
  fetchExtractedData,
  selectTemplateField,
  setCanvasMode,
  setTemplateFields,
  templateLoad,
  templateSetData,
  templateSetError,
} from "../../TemplateField";
import { TemplateFieldState } from "../../TemplateFieldTypes";
import {
  mapTemplateFieldFromClientToServer,
  mapTemplateFieldFromServerToClient,
} from "../../TemplateFieldUtils";

export const createTemplateFn = ({ history, action }) => async (
  dispatch,
  getState
) => {
  const reduxState: ReduxState = getState();
  const state: TemplateFieldState = reduxState.TemplateField;

  if (!state.template.template) {
    return;
  }

  let document: Document | undefined;
  const files = getState().Files.files;
  if (files.length > 0) {
    document = files[0].document;
  }

  if (!document) {
    return;
  }

  const { name, type, description, category } = state.template.template;
  const {
    fields,
    intuitNumber,
    logoURL,
    visibility,
  } = reduxState.TemplateMetadataFields;
  try {
    dispatch(templateLoad());
    const template = await createTemplate({
      name: name,
      metadataFields: fields
        .map((f) => f.field)
        .filter(
          (f) =>
            f &&
            f.documents &&
            f.documents.length > 0 &&
            f.documents[0].regexes &&
            f.documents[0].regexes.length > 0 &&
            f.documents[0].regexes[0].regexes &&
            f.documents[0].regexes[0].regexes.length > 0 &&
            f.documents[0].regexes[0].regexes[0]
        ),
      logoURL,
      intuitNumber,
      visibility,
      category: category || "",
      type: type || "FreeForm",
      description: description || "",
      version: "v2",
      template: JSON.stringify({
        rectangles: state.templateFields.map((tf) =>
          mapTemplateFieldFromClientToServer(tf)
        ),
      }),
      templateDocumentId: document.id,
    });
    dispatch(templateSetData({ template }));
    if (action === "writePdf") {
      history.push(
        `/writePdf?template=${template.name}&templateId=${template.id}`
      );
    } else {
      history.push(`/extractdata/custom?templateId=${template.id}`);
    }
  } catch (err) {
    logErrorToGA(err);
    const msg = "Error creating template";
    dispatch(templateSetError(msg));
    dispatch(
      createAlert({
        message: msg,
        timeout: 0,
        type: "error",
      })
    );
  }
};

export const updateTemplateFn = ({ history, action }) => async (
  dispatch,
  getState
) => {
  const state: TemplateFieldState = getState().TemplateField;

  if (!state.template.template) {
    return;
  }

  const { id } = state.template.template;
  try {
    dispatch(templateLoad());

    const template = await updateTemplate(id, {
      template: JSON.stringify({
        rectangles: state.templateFields.map((tf) =>
          mapTemplateFieldFromClientToServer(tf)
        ),
      }),
      version: "v2",
    });

    dispatch(templateSetData({ template }));
    if (action === "writePdf") {
      history.push(
        `/writePdf?template=${template.name}&templateId=${template.id}`
      );
    } else {
      history.push(`/extractdata/custom?templateId=${template.id}`);
    }
  } catch (err) {
    logErrorToGA(err);
    const msg = "Error updating template";
    dispatch(templateSetError(msg));
    dispatch(
      createAlert({
        message: msg,
        timeout: 0,
        type: "error",
      })
    );
  }
};

export const loadTemplateFn = ({ id }: { id: number }) => async (
  dispatch,
  getState
) => {
  let state: TemplateFieldState = getState().TemplateField;

  if (state.template.loading) {
    return;
  }
  try {
    dispatch(templateLoad());

    dispatch(setCanvasMode({ canvasMode: "rectangles" }));
    dispatch(deleteAllFields());

    const template = await getTemplate(id);
    const templateObj = JSON.parse(template.template);
    dispatch(templateSetData({ template }));
    dispatch(setDocument({ document: template.document })); // set document

    state = getState().TemplateField;
    let i = 0;
    while ((!state.stage || state.page.loading) && i < 200) {
      await sleep(50);
      state = getState().TemplateField;
      i += 1;
    }

    dispatch(
      setTemplateFields(
        templateObj.rectangles.map((f) =>
          mapTemplateFieldFromServerToClient(f, state, dispatch)
        )
      )
    ); // set document
    if (templateObj.rectangles.length > 0) {
      dispatch(selectTemplateField({ id: templateObj.rectangles[0].id }));
      dispatch(fetchExtractedData({ id: templateObj.rectangles[0].id }));
      for (const edge of ["top", "bottom", "left", "right"]) {
        dispatch(
          fetchEdgesFromWords({
            id: templateObj.rectangles[0].id,
            edge: edge as EdgeType,
          })
        );
      }
    }
  } catch (err) {
    logErrorToGA(err);
    const msg = "Error loading template";
    dispatch(templateSetError(msg));
    dispatch(
      createAlert({
        message: msg,
        timeout: 0,
        type: "error",
      })
    );
  }
};
